package net.sergeych.ugdx.shared.trader

import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.emitAll
import kotlinx.coroutines.flow.flow
import kotlinx.serialization.Serializable
import net.sergeych.ugdx.shared.Currency

/**
 * Currency pair, serializable, can be used as hash key. See [Label].
 */
@Serializable
data class CurrencyPair(
    val base: Currency,
    val quote: Currency,
    val basePrecision: Int = 3,
    val quotePrecision: Int = 3,
) {

    val label by lazy { "$base/$quote" }

    override fun toString(): String = label

    /**
     * Get all orders/all active orders for this currency pair as a flow, most recent first, tp avoid
     * pagination problems.
     * @param onlyActive include only orders that are active (e.g. not processed or queued in orderbook, See
     *                   [TradeOrder.Status.isSuitableForPorcessing]
     */
    fun allOrders(onlyActive: Boolean = true): Flow<TradeOrder> = flow {
        val lastId: Long? = null
        do {
            val result = Trader.getOrders(this@CurrencyPair, lastId, onlyActive = onlyActive)
            for( r in result.tradeOrders) emit(r)
        } while(result.hasMore)
    }

    /**
     * Get all order that are relevant to any curreny from this pair. It could be useful to calculate "positions"
     * that may compete for the same balance on user account.
     */
    @Suppress("unused")
    fun allRelevantOrders(onlyActive: Boolean = true): Flow<TradeOrder> = flow {
        Trader.currencyPairs().filter {
            base in it || quote in it
        }.forEach {
            emitAll(it.allOrders(onlyActive))
        }
    }

    /**
     * Check that some currency is used in this pair. Syntax sugar.
     */
    private operator fun contains(currency: Currency): Boolean {
        return base == currency || quote == currency
    }

    override fun equals(other: Any?): Boolean {
        return other is CurrencyPair && other.label == this.label
    }

    override fun hashCode(): Int = label.hashCode()

    companion object {
        /**
         * Get all orders for all currency pairs
         */
        fun allOrders(onlyActive: Boolean = true): Flow<TradeOrder> = flow{
            Trader.currencyPairs().forEach {
                emitAll(it.allOrders(onlyActive))
            }
        }
    }

}
